import { apiClient } from './apiClient';

export type PermissionGrantEffect = 'none' | 'allow' | 'deny';

export type PermissionGroup = {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  type: string;
  isActive: boolean;
};

export type PermissionGroupGrant = {
  moduleCode: string;
  moduleName?: string | null;
  accessEffect: PermissionGrantEffect;
  readEffect: PermissionGrantEffect;
  writeEffect: PermissionGrantEffect;
  deleteEffect: PermissionGrantEffect;
};

export type PermissionGroupListResult = {
  items: PermissionGroup[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type PermissionGroupUpsertPayload = {
  code: string;
  name: string;
  description?: string;
  isActive?: boolean;
};

export type SystemModuleSummary = {
  id: string;
  code: string;
  name: string;
  routePath: string;
  pillar: string;
};

function normalizeGrant(raw: Partial<PermissionGroupGrant> & {
  canRead?: boolean;
  canWrite?: boolean;
  canDelete?: boolean;
}): PermissionGroupGrant {
  const readEffect = raw.readEffect ?? (raw.canRead ? 'allow' : 'none');
  return {
    moduleCode: raw.moduleCode ?? '',
    moduleName: raw.moduleName,
    // Legado: se a API ainda não enviar accessEffect, espelha leitura (comportamento anterior).
    accessEffect: raw.accessEffect ?? readEffect,
    readEffect,
    writeEffect: raw.writeEffect ?? (raw.canWrite ? 'allow' : 'none'),
    deleteEffect: raw.deleteEffect ?? (raw.canDelete ? 'allow' : 'none'),
  };
}

export async function listPermissionGroups(params?: {
  page?: number;
  pageSize?: number;
}): Promise<PermissionGroupListResult> {
  const response = await apiClient.get<PermissionGroupListResult>('/v1/access-control/permission-groups', {
    params,
  });
  return response.data;
}

export async function listActivePermissionGroups(): Promise<PermissionGroup[]> {
  const response = await apiClient.get<PermissionGroup[]>('/v1/access-control/permission-groups/active');
  return response.data;
}

export async function getPermissionGroup(id: string): Promise<PermissionGroup> {
  const response = await apiClient.get<PermissionGroup>(`/v1/access-control/permission-groups/${id}`);
  return response.data;
}

export async function getPermissionGroupGrants(id: string): Promise<PermissionGroupGrant[]> {
  const response = await apiClient.get<Partial<PermissionGroupGrant>[]>(
    `/v1/access-control/permission-groups/${id}/grants`,
  );
  return response.data.map((grant) => normalizeGrant(grant));
}

export async function listPermissionGroupModuleCatalog(): Promise<SystemModuleSummary[]> {
  const response = await apiClient.get<SystemModuleSummary[]>(
    '/v1/access-control/permission-groups/module-catalog',
  );
  return response.data;
}

export async function createPermissionGroup(payload: PermissionGroupUpsertPayload): Promise<PermissionGroup> {
  const response = await apiClient.post<PermissionGroup>('/v1/access-control/permission-groups', payload);
  return response.data;
}

export async function updatePermissionGroup(
  id: string,
  payload: PermissionGroupUpsertPayload,
): Promise<PermissionGroup> {
  const response = await apiClient.put<PermissionGroup>(`/v1/access-control/permission-groups/${id}`, payload);
  return response.data;
}

export async function deletePermissionGroup(id: string): Promise<void> {
  await apiClient.delete(`/v1/access-control/permission-groups/${id}`);
}

export async function replacePermissionGroupGrants(
  id: string,
  grants: PermissionGroupGrant[],
): Promise<void> {
  await apiClient.put(`/v1/access-control/permission-groups/${id}/grants`, { grants });
}
